import type {ReactElement} from 'react';
import {useFormContext} from 'react-hook-form';
import {SurveyFieldKind, SURVEY_FIELD_ERROR_ID_PREFIX} from '@Components/ai-chat-window/components/survey/survey.types';
import {Text, TextSize} from '@Components/text';
import type {FieldRendererProps} from './fields.types';
import {TextFieldRenderer} from './text-field';
import {SelectFieldRenderer} from './select-field';
import {MultiSelectFieldRenderer} from './multiselect-field';
import styles from './fields.module.scss';

export function FieldRenderer({step}: FieldRendererProps): ReactElement | null {
  const name = step.key;
  const {formState} = useFormContext();

  /**
   * The element every field's aria-describedby already points at. Without it the min/max message a
   * validator returns has nowhere to render, so Next simply stops working with nothing said.
   */
  function renderError(): ReactElement | null {
    const message = formState.errors[name]?.message;
    if (typeof message !== 'string' || message === '') {
      return null;
    }
    return (
      <Text attributes={{id: `${SURVEY_FIELD_ERROR_ID_PREFIX}${name}`, role: 'alert'}} className={styles.fieldError} size={TextSize.SM} val={message} />
    );
  }

  function renderField(): ReactElement | null {
    switch (step.field.kind) {
      case SurveyFieldKind.TEXT:
        return <TextFieldRenderer name={name} step={step} field={step.field} />;
      case SurveyFieldKind.SELECT:
        return <SelectFieldRenderer name={name} step={step} field={step.field} />;
      case SurveyFieldKind.MULTISELECT:
        return <MultiSelectFieldRenderer name={name} step={step} field={step.field} />;
      default:
        return null;
    }
  }

  return (
    <>
      {renderField()}
      {renderError()}
    </>
  );
}
